import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { requestPasswordReset, resetPassword } from '../repository/authRepository';

const PasswordReset: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (token) {
      setMessage('Please enter your new password.');
    }
  }, [token]);

  const handleRequestReset = async (event: React.FormEvent) => {
    event.preventDefault();
    setMessage(null);
    setError(null);
    setLoading(true);

    try {
      await requestPasswordReset(email);
      setMessage('If an account with that email exists, a password reset link has been sent.');
    } catch (err: any) {
      setError(err.message || 'Failed to request password reset.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (event: React.FormEvent) => {
    event.preventDefault();
    setMessage(null);
    setError(null);
    setLoading(true);

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      setLoading(false);
      return;
    }

    if (!token) {
      setError('No reset token found.');
      setLoading(false);
      return;
    }

    try {
      await resetPassword(token, newPassword);
      setMessage('Your password has been successfully reset. You can now log in.');
    } catch (err: any) {
      setError(err.message || 'Failed to reset password.');
    } finally {
      setLoading(false);
    }
  };

  if (token) {
    return (
      <div className="auth-shell">
        <div className="auth-card">
          <div className="auth-hero">
            <h2 className="auth-title">Reset Password</h2>
            <p className="auth-subtitle">Choose a new password for your account.</p>
          </div>
          <form onSubmit={handleResetPassword}>
            <div className="field">
              <label className="field-label" htmlFor="newPassword">New Password</label>
              <input
                className="input"
                type="password"
                id="newPassword"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
              />
            </div>
            <div className="field">
              <label className="field-label" htmlFor="confirmPassword">Confirm New Password</label>
              <input
                className="input"
                type="password"
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
            </div>
            {message && <div className="alert success">{message}</div>}
            {error && <div className="alert error">{error}</div>}
            <button className="button" type="submit" disabled={loading} style={{ width: '100%' }}>
              {loading ? 'Resetting...' : 'Reset Password'}
            </button>
          </form>
          <div className="auth-links">
            <a href="/login">Back to Login</a>
          </div>
        </div>
      </div>
    );
  } else {
    return (
      <div className="auth-shell">
        <div className="auth-card">
          <div className="auth-hero">
            <h2 className="auth-title">Forgot your password?</h2>
            <p className="auth-subtitle">No worries — enter your email and we'll send you a reset link.</p>
          </div>
          <form onSubmit={handleRequestReset}>
            <div className="field">
              <label className="field-label" htmlFor="email">Email</label>
              <input
                className="input"
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            {message && <div className="alert success">{message}</div>}
            {error && <div className="alert error">{error}</div>}
            <button className="button" type="submit" disabled={loading} style={{ width: '100%' }}>
              {loading ? 'Sending...' : 'Send Reset Link'}
            </button>
          </form>
          <div className="auth-links">
            <a href="/login">Back to Login</a>
          </div>
        </div>
      </div>
    );
  }
};

export default PasswordReset;
